import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * MetricCard / StatRow / Stat (Playbook § Padrões de casa).
 *
 * `MetricCard` é card próprio — usar uma vez por indicador. `StatRow`/`Stat`
 * agrupam vários indicadores dentro de um único card, sem moldura individual.
 * O rótulo é PT Sans Narrow em caixa alta e o valor é PT Sans Narrow 700, como
 * no sustenta-ai-app; a diferença de largura entre as duas fontes é o que
 * separa visualmente rótulo de número sem precisar de cor.
 *
 * `valor === null` NÃO vira zero: o card inteiro passa a tracejado sobre
 * `bg-subtle` e diz de onde o dado deveria vir — a quinta regra, implícita,
 * dos quatro estados.
 */

interface MetricProps {
  rotulo: string;
  valor: string | number | null;
  /** Origem esperada do dado, exibida quando `valor` é nulo. */
  fonte?: string;
  descricao?: React.ReactNode;
  icone?: React.ReactNode;
  className?: string;
}

function Valor({ valor, fonte }: { valor: string | number | null; fonte?: string }) {
  if (valor === null || valor === undefined || valor === '') {
    return (
      <span
        className="text-body text-on-surface-muted"
        title={fonte ? `Este indicador depende de ${fonte}, ainda não integrado.` : undefined}
      >
        Sem dado{fonte ? ` · aguarda ${fonte}` : ''}
      </span>
    );
  }
  return <span className="font-narrow text-kpi tabular-nums">{valor}</span>;
}

export function MetricCard({ rotulo, valor, fonte, descricao, icone, className }: MetricProps) {
  const ausente = valor === null || valor === undefined || valor === '';

  return (
    <div
      className={cn(
        'flex flex-col gap-1 rounded-card border p-4',
        ausente ? 'pan-pending' : 'border-border bg-bg-surface shadow-sm',
        className,
      )}
    >
      <div className="flex items-center gap-1.5 text-on-surface-muted">
        {icone}
        <span className="pan-eyebrow">{rotulo}</span>
      </div>
      <Valor valor={valor} fonte={fonte} />
      {descricao ? <p className="text-cell text-on-surface-muted">{descricao}</p> : null}
    </div>
  );
}

/**
 * Vários indicadores num painel só, separados por régua — não quatro cards
 * soltos. Playbook § Padrões de casa: `MetricCard` é card próprio (usar 1×);
 * `StatRow`/`Stat` agrupam indicadores relacionados sem moldura individual.
 * Quatro números sobre o MESMO escopo são um grupo, não quatro objetos.
 */
export function StatRow({
  children,
  colunas = 4,
  className,
}: {
  children: React.ReactNode;
  colunas?: 3 | 4;
  className?: string;
}) {
  return (
    <dl
      className={cn(
        'grid grid-cols-1 divide-y divide-border-subtle sm:grid-cols-2 sm:divide-y-0 lg:divide-x',
        colunas === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3',
        'sm:[&>*:nth-child(-n+2)]:border-b sm:[&>*]:border-border-subtle lg:[&>*]:border-b-0',
        className,
      )}
    >
      {children}
    </dl>
  );
}

export function Stat({ rotulo, valor, fonte, descricao, icone }: MetricProps) {
  return (
    <div className="flex flex-col gap-1 px-5 py-4">
      <dt className="flex items-center gap-1.5 text-on-surface-muted">
        {icone}
        <span className="pan-eyebrow">{rotulo}</span>
      </dt>
      <dd className="flex flex-col gap-0.5">
        <Valor valor={valor} fonte={fonte} />
        {descricao ? <span className="text-cell text-on-surface-muted">{descricao}</span> : null}
      </dd>
    </div>
  );
}

/** Par rótulo/valor textual, para painéis de detalhe. */
export function DetalheItem({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="pan-eyebrow">{rotulo}</dt>
      <dd className="text-body text-on-surface">{children}</dd>
    </div>
  );
}

export function DetalheLista({ children, className }: { children: React.ReactNode; className?: string }) {
  return <dl className={cn('grid grid-cols-2 gap-x-6 gap-y-4', className)}>{children}</dl>;
}
